import {
  CreateDocumentMutation,
  DocumentType,
} from "@src/__generated__/graphql";
import { createContext, useState } from "react";
import { useS3Upload, useVendorPortalS3Upload } from "@src/hooks/useS3Upload";
import { useCreateDocument } from "@src/pages/document/useCreateDocument";
import { stripQuery } from "@src/utils/string";
import { useSudoziVendorMutation } from "@src/infrastructure/useSudoziMutation";
import { PostVendorPortalDocumentsRoute } from "@src/sudozi-schemas/api-route-schemas";
import { z } from "zod";

export interface FileWithUrl {
  file: File;
  url: string;
  fields: Record<string, string>;
}

export const DocumentContext = createContext<
  CreateDocumentMutation[] | undefined
>(undefined);

export interface FileWithDocType extends FileWithUrl {
  docType: DocumentType[];
}

export function useDocumentUpload() {
  const [loading, setLoading] = useState(false);
  const { createDocument } = useCreateDocument();
  const { upload } = useS3Upload();

  async function addFiles({
    files,
    forceDocumentTypes,
    serviceIds,
  }: {
    files: File[];
    serviceIds?: string[];
    forceDocumentTypes?: `${DocumentType}`[];
  }) {
    setLoading(true);
    const asyncFiles = files.map(async (file) => {
      const fileWithUploadUrl = await upload(file);
      if (!fileWithUploadUrl) {
        setLoading(false);
        return;
      }

      const fetchResult = await createDocument({
        variables: {
          input: {
            name: fileWithUploadUrl.file.name,
            displayName: file.name,
            type: forceDocumentTypes
              ? forceDocumentTypes.map((t) => DocumentType[t])
              : fileWithUploadUrl.docType,
            mimeType: fileWithUploadUrl.file.type,
            url: stripQuery(fileWithUploadUrl.url),
            relatedServiceIds: serviceIds,
          },
        },
      });
      return fetchResult.data;
    });
    const results = await Promise.all(asyncFiles);
    const filtered = results.filter(
      (result) => !!result,
    ) as CreateDocumentMutation[];
    setLoading(false);
    return filtered;
  }

  return {
    loading,
    addFiles,
  };
}

export function useVendorPortalDocumentUpload() {
  const [loading, setLoading] = useState(false);
  const saveDocsHandler = useSudoziVendorMutation({
    route: PostVendorPortalDocumentsRoute,
    mutationKey: ["POST /api/v/documents"],
  });
  const { upload } = useVendorPortalS3Upload();

  async function addFiles({
    files,
    forceDocumentTypes,
  }: {
    files: File[];
    forceDocumentTypes?: `${DocumentType}`[];
  }) {
    setLoading(true);
    const asyncFiles = files.map(async (file) => {
      const fileWithUploadUrl = await upload(file);
      if (!fileWithUploadUrl) {
        setLoading(false);
        return;
      }

      const fetchResult = await saveDocsHandler.mutateAsync({
        body: {
          name: fileWithUploadUrl.file.name,
          displayName: file.name,
          type: forceDocumentTypes ?? fileWithUploadUrl.docType,
          mimeType: fileWithUploadUrl.file.type,
          url: stripQuery(fileWithUploadUrl.url),
        },
      });
      return fetchResult;
    });
    const results = await Promise.all(asyncFiles);
    const filtered = results.filter((result) => !!result) as z.infer<
      typeof PostVendorPortalDocumentsRoute.output
    >[];
    setLoading(false);
    return filtered;
  }

  return {
    loading,
    addFiles,
  };
}
